<template>
  <section
    class="card"
    :class="{ create: create }"
    :id="create ? 'create-connector' : ''"
    @click="handleClickCreate"
  >
    <template v-if="create">
      <div class="info">
        <PlusOutlined class="icon" />
        <span>Integrar conector de API</span>
      </div>
    </template>

    <template v-else>
      <header class="card__header">
        <div class="card__channels">
          <p>
            {{ countConnections }}
            {{ countConnections === 1 ? "conexión" : "conexiones" }}

            <a-popover 
              v-model:open="visibleConnectionsPopover"
              class="popover"
              :title="`Conexiones (${countConnections})`"
            >
              <template #content>
                <ul class="connector_connections">
                  <li
                    v-for="connection in firstConnections"
                    :key="connection.id"
                  >
                    <div class="connection-info">
                      <a-tooltip
                        :title="connection?.name"
                        :arrow="false"
                        overlayClassName="custom-tooltip"
                        placement="bottom"
                      >
                        <span class="connection-name">{{ connection.name }}</span>
                      </a-tooltip>
                      <span class="connection-type">{{ ConnectionTypeText[connection.type as ConnectionType] }}</span>
                    </div>
                    <a-tag class="connection-status" :color="getStatusColor(connection.status)" style="margin: 0">{{ getStatusText(connection.status) }}</a-tag>
                  </li>
                </ul>
                <a-divider v-if="firstConnections.length < countConnections" style="margin: 10px 0" />
                <div class="popover__footer" v-if="firstConnections.length < countConnections">
                  <a-button type="link" @click="handleViewConnections">
                    <EyeOutlined /> Ver {{ countConnections - firstConnections.length }} más
                  </a-button>
                </div>
              </template>

              <InfoCircleOutlined
                style="margin-left: 2px; font-size: 14px"
              />
            </a-popover>
          </p>
        </div>

        <div class="card__menu">
          <a-tooltip title="Ver detalles">
            <a-button class="button" type="link">
              <EyeOutlined class="card__icon" @click="handleViewDetail" />
            </a-button>
          </a-tooltip>

          <a-tooltip>
            <template #title>
              <span v-if="disabledAction">
                No puedes editar este conector  mientras esté activo en un IVR. Inactívalos para poder editarlo.
              </span>
              <span v-else>
                Editar
              </span>
            </template>
            <a-button class="button" type="link" :disabled="disabledAction">
              <EditOutlined class="card__icon" @click="handleEdit" />
            </a-button>
          </a-tooltip>

          <a-tooltip>
            <template #title>
              <span v-if="disabledAction">
                No puedes eliminar este conector  mientras esté activo en un IVR. Inactívalos para poder eliminarlo.
              </span>
              <span v-else>
                Eliminar
              </span>
            </template>
            <a-button class="button" type="link" :disabled="disabledAction" danger>
              <DeleteOutlined
                class="card__icon"
                @click="handleDelete"
              />
            </a-button>
          </a-tooltip>
        </div>
      </header>

      <div style="height: 100%; position: relative">
        <a-tooltip
          :title="connector?.name"
          :arrow="false"
          overlayClassName="custom-tooltip"
          placement="bottom"
        >
          <h4 class="card__name">{{ connector?.name }}</h4>
        </a-tooltip>

        <span class="card__description">
          {{ truncatedDescription }}
          {{ showMoreButton ? '... ' : '' }}
          <span v-if="showMoreButton" class="see-more" @click.stop="handleViewDetail">
            Ver más
          </span>
        </span>      

      </div>

      <footer class="card__footer">
        <div>
          Creado el {{ dateToString(connector?.created_at as Date, "DD/MM/YYYY") }}
        </div>
      </footer>
    </template>
  </section>
  
  <DeleteConnector v-model:open="openDeleteConnector" :connector="connector" @update="handleUpdate" />
  
  <ConnectorDetailModal
    v-if="openDetailModal"
    v-model:open="openDetailModal"
    :connector="connector"
  />

  <ConnectionsConnectorModal v-if="openConnectionsModal" v-model:open="openConnectionsModal" :connector="connector" />
</template>

<script setup lang="ts">
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons-vue";

import { Connector } from "@/@types/connection";
import { computed, ref } from "vue";

import DeleteConnector from "../modals/DeleteConnector.vue";
import ConnectorDetailModal from "../modals/ConnectorDetailModal.vue";
import { dateToString } from "@/app/shared/utils/dates";
import { BotStatus, ConnectionType, ConnectionTypeText } from "../../utils/enums";
import ConnectionsConnectorModal from "@/app/ivr/bot/components/modals/ConnectionsConnectorModal.vue";
import { Modal } from "ant-design-vue";

const props = defineProps<{ create?: boolean; connector: Connector }>();

const emit = defineEmits<{
  (e: "create"): void;
  (e: "edit"): void;
  (e: "delete"): void;
  (e: "update"): void;
}>();

const handleClickCreate = () => {
  if (!props.create) return;

  emit("create");
};

const openDetailModal = ref(false);
const visibleConnectionsPopover = ref(false);
const openConnectionsModal = ref(false);

const MAX_CHARS = 145

const truncatedDescription = computed(() => {
  const text = props.connector?.description || "";
  if (text.length <= MAX_CHARS) return text;
  return text.slice(0, MAX_CHARS);
});

const showMoreButton = computed(() => {
  return (props.connector?.description?.length || 0) > MAX_CHARS;
});

const countConnections = computed(() => props.connector?.connections?.length || 0)

const firstConnections = computed(() => {
  return props.connector?.connections?.slice(0,5) || [];
});

const handleViewConnections = () => {
  openConnectionsModal.value = true;
  visibleConnectionsPopover.value = false;
};

const handleViewDetail = () => {
  openDetailModal.value = true;
};

const handleEdit = () => {
  if (props.connector.connection?.length === 0) {
    emit("edit");
    return;
  }
  Modal.confirm({
    title: '¿Deseas realizar cambios a tu conector de API?',
    content: 'Recuerda que al editar, se perderán las configuraciones en todos los IVR operadores automáticos y/o chatbots de WhatsApp vinculados.',
    okText: 'Sí, editar',
    width: 450,
    centered: true,
    cancelText: 'Cancelar',
    onOk() {
      emit("edit");
    },
  });
};

const openDeleteConnector = ref(false);

const handleDelete = () => {
  openDeleteConnector.value = true;
};

const disabledAction = computed(() => {
  return props.connector.connection?.find((el: any) => el.status == BotStatus.Active);
});

const getStatusColor = (status: number) => {
  if (status === BotStatus.Active) {
    return "green";
  }
  return "default";
};

const getStatusText = (status: number) => {
  if (status === BotStatus.Active) {
    return "Activo";
  }
  if (status === BotStatus.Inactive) {
    return "Inactivo";
  }
  return "";
};

const handleUpdate = () => emit("update");

</script>

<style scoped lang="sass">
p
  margin: 0

.button
  padding: 0
  height: 22px

.card
  height: 204px
  // background: $gray-2
  border-radius: 4px
  border: $border-medium
  position: relative
  padding: 12px 20px
  display: grid
  grid-template-rows: auto 1fr auto
  grid-template-columns: 100%

  &.create
    display: flex
    align-items: center
    justify-content: center
    border-color: $blue-6
    color: $blue-6
    border-style: dashed
    background-color: $blue-1
    cursor: pointer

    .info
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      color: $blue-6

      .icon
        color: $blue-6
        font-size: 40px
        margin-bottom: 8px

  &__header
    display: flex
    justify-content: space-between
    align-items: center
    margin-bottom: 10px

  &__channels
    color: $gray-8
    font-size: 13px
    display: flex
    align-items: center
    gap: 4px

    img
      width: 18px

  &__menu
    display: flex
    justify-content: flex-end
    gap: 12px

  &__name
    font-size: 15px
    color: $gray-9
    margin-bottom: 2px
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    display: block

  &__description
    color: $gray-1
    word-wrap: break-word
    font-size: 13px
    line-height: 1.4

  &__footer
    padding: 12px 0 0
    // border-top: 1px solid $gray-5
    display: flex
    justify-content: space-between
    align-items: center
    color: $gray-7
    font-style: italic
    font-size: 12px

.connector_connections
  list-style: none
  padding: 0
  margin: 0
  li
    display: flex
    justify-content: space-between
    align-items: center
    margin-top: 10px
    gap: 10px

  .connection-info
    display: flex
    flex-direction: column
    .connection-type
      color: $text-tertiary

  .connection-status
    height: min-content

.see-more
  color: $blue-6
  cursor: pointer
  // margin-left: 4px

.connection-name
  max-width: 150px
  overflow: hidden
  white-space: nowrap
  text-overflow: ellipsis
  display: inline-block

.popover
  &__footer
    width: 100%
    display: flex
    justify-content: center
</style>
